import { addDays, addMonths, addWeeks, differenceInCalendarDays, endOfMonth, format, parse, startOfMonth, startOfWeek } from 'date-fns';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import { ShiftPatternEntity } from 'src/entities/ShiftPattern.entity';
import { TruckTypes } from 'src/entities/truckEntity';
import { TruckShiftEntity, TruckShiftShiftEntity } from 'src/entities/TruckShift.entity';

// 車格フィルタは車両マスタと同じ TruckTypes（軽貨物→2t→4t→10t→20t）順で表示する。未知の車格は末尾。
const klassRank = (klass: string): number => {
  const index = TruckTypes.indexOf(klass);
  return index === -1 ? TruckTypes.length : index;
};

export type PeriodMode = 'week' | 'month';
export type DateRange = { startOn: string; endOn: string };

const DATE_FMT = 'yyyy-MM-dd';
const toStr = (date: Date): string => format(date, DATE_FMT);
const toDate = (value: string): Date => parse(value, DATE_FMT, new Date());
const isValidDate = (date: Date): boolean => !Number.isNaN(date.getTime());

export const normalizePeriod = (period: PeriodMode, anchor: string): DateRange => {
  const date = toDate(anchor);
  if (period === 'month') {
    return { startOn: toStr(startOfMonth(date)), endOn: toStr(endOfMonth(date)) };
  }
  // week: anchor を含む月曜始まりの週に正規化（/shifts と同じ weekStartsOn:1）。
  const monday = startOfWeek(date, { weekStartsOn: 1 });
  return { startOn: toStr(monday), endOn: toStr(addDays(monday, 6)) };
};

// ツールバーの期間ラベル。月は「YYYY年M月 (N日間)」、週は「YYYY-MM-DD 〜 YYYY-MM-DD」。
export const periodLabel = (period: PeriodMode, startOn: string, endOn: string): string => {
  if (period === 'month') {
    const date = toDate(startOn);
    const days = differenceInCalendarDays(toDate(endOn), date) + 1;
    return `${date.getFullYear()}年${date.getMonth() + 1}月 (${days}日間)`;
  }
  return `${startOn} 〜 ${endOn}`;
};

export const derivePeriod = (startOn: string, endOn: string): PeriodMode => {
  const start = toDate(startOn);
  const end = toDate(endOn);
  if (!isValidDate(start) || !isValidDate(end)) return 'week';
  if (toStr(startOfMonth(start)) === startOn && toStr(endOfMonth(start)) === endOn) return 'month';
  return 'week';
};

export const isPeriodMode = (value: unknown): value is PeriodMode => value === 'week' || value === 'month';

// 週/月 表示モードの永続化 localStorage キー（usePersistedJSON と共有）。
export const PERIOD_STORAGE_KEY = 'truckShifts.periodMode';

// 永続化された表示モードを読む（未保存/不正は week）。フック外（遷移リンク生成時）から同期参照するため。
export const readPreferredPeriod = (): PeriodMode => {
  try {
    const stored = localStorage.getItem(PERIOD_STORAGE_KEY);
    if (stored === null) return 'week';
    const parsed: unknown = JSON.parse(stored);
    return isPeriodMode(parsed) ? parsed : 'week';
  } catch {
    return 'week';
  }
};

// /truck-shifts の URL を組み立てる唯一の場所。startOn/endOn/backTo(省略可) から生成する。
export const buildTruckShiftsPath = ({ startOn, endOn, backTo }: { startOn: string; endOn: string; backTo?: string }): string =>
  ['/truck-shifts', startOn, endOn, backTo].filter(Boolean).join('/');

// 配車画面など外部から稼働表を開くときのパス。アンカー日 + 永続化した週/月モードで範囲を作る。
export const truckShiftsPathForAnchor = (anchor: string, backTo?: string): string => {
  const { startOn, endOn } = normalizePeriod(readPreferredPeriod(), anchor);
  return buildTruckShiftsPath({ startOn, endOn, backTo });
};

export const isNullableNumber = (value: unknown): value is number | null => value === null || typeof value === 'number';

// 永続化した選択便IDを現在の便一覧で検証する。存在しない（会社切替・削除）なら未選択(null)に戻す。
export const sanitizePatternId = (id: number | null, patterns: { id: number }[]): number | null =>
  (id != null && patterns.some((p) => p.id === id) ? id : null);

// 便プルダウンの選択を解決する。前回選択が有効ならそれを維持し、無効/未選択なら
// 既定名の便（例: 全日便）にフォールバック。既定名が無ければ null。
export const resolvePatternSelection = (
  prev: number | null,
  patterns: { id: number; name: string }[],
  defaultName: string,
): number | null => {
  const valid = sanitizePatternId(prev, patterns);
  if (valid !== null) return valid;
  return patterns.find((p) => p.name === defaultName)?.id ?? null;
};

export const canonicalRangeFromUrl = (
  startOn: string | undefined,
  endOn: string | undefined,
  fallbackAnchor: string,
  fallbackPeriod: PeriodMode = 'week',
): { period: PeriodMode; range: DateRange } => {
  // 範囲未指定（初回入場）は永続化した表示モード(fallbackPeriod)を既定にする。
  // 範囲が明示された URL は period を URL から導出し優先する（共有・ブックマークを壊さない）。
  if (!startOn || !isValidDate(toDate(startOn))) {
    return { period: fallbackPeriod, range: normalizePeriod(fallbackPeriod, fallbackAnchor) };
  }
  const period = (!!endOn && isValidDate(toDate(endOn))) ? derivePeriod(startOn, endOn) : 'week';
  return { period, range: normalizePeriod(period, startOn) };
};

export const buildDateColumns = (startOn: string, endOn: string): string[] => {
  const columns: string[] = [];
  let cursor = toDate(startOn);
  const end = toDate(endOn);
  while (cursor <= end) {
    columns.push(toStr(cursor));
    cursor = addDays(cursor, 1);
  }
  return columns;
};

export const shiftRange = (period: PeriodMode, startOn: string, direction: 1 | -1): DateRange => {
  const moved = period === 'month' ? addMonths(toDate(startOn), direction) : addWeeks(toDate(startOn), direction);
  return normalizePeriod(period, toStr(moved));
};

export const toggleRange = (nextPeriod: PeriodMode, currentStartOn: string): DateRange =>
  normalizePeriod(nextPeriod, currentStartOn);

export const NO_PARTNER_LABEL = '運送会社未設定';

export type CompanyGroup = {
  companyId: number | null;
  companyName: string;
  trucks: TruckShiftEntity[];
};

export const groupByPartnerCompany = (trucks: TruckShiftEntity[]): CompanyGroup[] => {
  const groups = new Map<number | null, CompanyGroup>();
  trucks.forEach((truck) => {
    const companyId = truck.partnerCompany?.id ?? null;
    const companyName = truck.partnerCompany?.name ?? NO_PARTNER_LABEL;
    const existing = groups.get(companyId);
    if (existing) {
      existing.trucks.push(truck);
    } else {
      groups.set(companyId, { companyId, companyName, trucks: [truck] });
    }
  });
  return Array.from(groups.values()).sort((a, b) => {
    if (a.companyId === null) return -1;
    if (b.companyId === null) return 1;
    return a.companyName.localeCompare(b.companyName, 'ja');
  });
};

export type TruckFilters = {
  partnerCompanyIds: (number | null)[];
  klasses: string[];
  search: string;
};

export const applyFilters = (trucks: TruckShiftEntity[], filters: TruckFilters): TruckShiftEntity[] => {
  const query = filters.search.trim().toLowerCase();
  return trucks.filter((truck) => {
    const companyOk = filters.partnerCompanyIds.length === 0
      || filters.partnerCompanyIds.includes(truck.partnerCompany?.id ?? null);
    const klassOk = filters.klasses.length === 0
      || (truck.klass !== null && filters.klasses.includes(truck.klass));
    const searchTargets = [
      truck.licensePlateValue,
      truck.truckNumber ?? '',
      truck.defaultDriver?.name ?? '',
      ...truck.shifts.map((shift) => shift.driver?.name ?? ''),
    ];
    const searchOk = query === '' || searchTargets.some((field) => field.toLowerCase().includes(query));
    return companyOk && klassOk && searchOk;
  });
};

export type FilterOptions = {
  companies: { id: number; name: string }[];
  klasses: string[];
  hasNoPartner: boolean;
};

export const deriveFilterOptions = (trucks: TruckShiftEntity[]): FilterOptions => {
  const companyMap = new Map<number, string>();
  const klassSet = new Set<string>();
  let hasNoPartner = false;
  trucks.forEach((truck) => {
    if (truck.partnerCompany) {
      companyMap.set(truck.partnerCompany.id, truck.partnerCompany.name);
    } else {
      hasNoPartner = true;
    }
    if (truck.klass !== null) klassSet.add(truck.klass);
  });
  return {
    companies: Array.from(companyMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'ja')),
    klasses: Array.from(klassSet).sort((a, b) => klassRank(a) - klassRank(b) || a.localeCompare(b, 'ja')),
    hasNoPartner,
  };
};

export type ShiftCell = {
  shift: TruckShiftShiftEntity;
  isContinuation: boolean;
  segment: 'full' | 'start' | 'middle' | 'end';
  spanEdge: 'start' | 'middle' | 'end' | null;
};
export type CellsByDate = Record<string, ShiftCell[]>;

export const jstDate = (iso: string): string => datetimeDecorator.toYyyyMmDd(new Date(iso));

// 選択中(truck × date)に重なる既存シフト数（distinct shift・クライアント推定）。
// グリッドは start_at 基準取得で表示期間前開始の食い込みを取りこぼし得る＝UI ヒント（権威は API 409）。
export const countSelectedShifts = (
  trucks: TruckShiftEntity[],
  selectedTruckIds: Set<number>,
  selectedDates: Set<string>,
): number => {
  if (selectedTruckIds.size === 0 || selectedDates.size === 0) return 0;
  let count = 0;
  trucks.forEach((truck) => {
    if (!selectedTruckIds.has(truck.id)) return;
    truck.shifts.forEach((shift) => { if (shiftCoversSelectedDate(shift, selectedDates)) count += 1; });
  });
  return count;
};

const shiftCoversSelectedDate = (shift: TruckShiftShiftEntity, selectedDates: Set<string>): boolean => {
  let cursor = toDate(jstDate(shift.startAt));
  const end = toDate(jstDate(shift.endAt));
  while (cursor <= end) {
    if (selectedDates.has(toStr(cursor))) return true;
    cursor = addDays(cursor, 1);
  }
  return false;
};

export const mapShiftsToCells = (shifts: TruckShiftShiftEntity[], dateColumns: string[]): CellsByDate => {
  const result: CellsByDate = {};
  dateColumns.forEach((date) => { result[date] = []; });

  const groupDates = new Map<number, Set<string>>();
  shifts.forEach((shift) => {
    if (shift.continuousShiftGroupId === null) return;
    const dates = groupDates.get(shift.continuousShiftGroupId) ?? new Set<string>();
    dates.add(jstDate(shift.startAt));
    groupDates.set(shift.continuousShiftGroupId, dates);
  });

  // 連続稼働帯における当該日の位置（下線の左右端用）。連続でなければ null。
  const chainSpanEdge = (date: string, groupId: number | null): 'start' | 'middle' | 'end' | null => {
    if (groupId === null) return null;
    const dates = groupDates.get(groupId);
    if (!dates || dates.size <= 1) return null;
    const hasPrev = dates.has(toStr(addDays(toDate(date), -1)));
    const hasNext = dates.has(toStr(addDays(toDate(date), 1)));
    if (!hasPrev) return 'start';
    if (!hasNext) return 'end';
    return 'middle';
  };

  shifts.forEach((shift) => {
    const startDate = jstDate(shift.startAt);
    const endDate = jstDate(shift.endAt);
    let isContinuation = false;
    if (shift.continuousShiftGroupId !== null) {
      const prev = toStr(addDays(toDate(startDate), -1));
      isContinuation = groupDates.get(shift.continuousShiftGroupId)?.has(prev) ?? false;
    }
    if (endDate > startDate) {
      // 複数日跨ぎ: 跨ぐ全日（表示中の dateColumns 内）に配置
      if (result[startDate]) result[startDate].push({ shift, isContinuation, segment: 'start', spanEdge: 'start' });
      if (result[endDate]) result[endDate].push({ shift, isContinuation: false, segment: 'end', spanEdge: 'end' });
      dateColumns.forEach((col) => {
        if (col > startDate && col < endDate) {
          result[col].push({ shift, isContinuation: false, segment: 'middle', spanEdge: 'middle' });
        }
      });
    } else if (result[startDate]) {
      // 同日終了（または終了日が開始日より前の異常データ）
      result[startDate].push({ shift, isContinuation, segment: 'full', spanEdge: chainSpanEdge(startDate, shift.continuousShiftGroupId) });
    }
  });

  Object.keys(result).forEach((date) => {
    result[date].sort((a, b) => a.shift.startAt.localeCompare(b.shift.startAt));
  });
  return result;
};

// A3b 打ち切りボタンの表示可否。pattern 由来 multi_day の単一 Shift で、cellDate がその shift の末日でないとき true。
// （shift.shiftPattern を allPatterns に照合して multiDay を判定。レガシー連続グループの打ち切り UI は A3b では対象外＝
//  新形式の単一 multi-day Shift のみに限定。API はレガシーも受けるが本ボタンからは出さない。）
export const isTruncatableAtDate = (
  shift: TruckShiftShiftEntity,
  cellDate: string,
  allPatterns: ShiftPatternEntity[],
): boolean => {
  if (shift.continuousShiftGroupId !== null) return false; // legacy 連続グループは A3b の FE スコープ外（単一 Shift のみ）
  const pattern = shift.shiftPattern ? allPatterns.find((p) => p.id === shift.shiftPattern?.id) : undefined;
  if (pattern?.multiDay !== true) return false;
  const start = jstDate(shift.startAt);
  const end = jstDate(shift.endAt);
  return start <= cellDate && cellDate < end; // 末日(end)は不可＝打ち切る suffix が無い
};
